import React, { useEffect, useState, useReducer } from "react";
import PropTypes from "kf-proptypes/main";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import "./polyfill.js";

import { NotAvailable } from "account/notavailable";
import { LOGIN_ROUTES, PUBLIC_ROUTES } from "account/login";
import { ThemeList, onThemeChange, applyAccountTheme } from "account/theme";
import { ConfigProvider } from "widgets/configprovider";
import { rtdbClient } from "base/rtdb";
import { pushNotificationClient } from "base/pushnotification";
import { KEY_PRIMARY_500 } from "base/constants";
import { getSandbox, loadFonts } from "base/utils";
import { useUnhandledRejectionListener } from "base/hooks";
import { useAccountStore, useUserStore } from "base/stores";
import { Theme } from "base/themes";
import { queryClient, ReactQueryDevTools } from "base/query";
import { I18nProvider, LANGUAGE_CODE, loadLanguage } from "base/i18n";
import "base/styles";

import {
  LoginRoute,
  AuthorizedComponents,
  PublicRoute,
  PrivateRoute
} from "./routes.js";

// eslint-disable-next-line kf/const-naming-convention
// const queryClient = new QueryClient();

// eslint-disable-next-line kf/const-naming-convention
// const queryClient = new QueryClient();

//to register the routes which are navigatable without login
export { PublicRoute, PrivateRoute };

BaseComponent.propTypes = {
  children: PropTypes.node.isRequired,
  baseName: PropTypes.string
};

export function BaseComponent({ children, baseName = "/view" }) {
  const [isFailed, setFailStatus] = useState(false);
  const [isLanguageInitialized, setLanguageInitialized] = useState(false);
  // const [isAuthendicated, setReady] = useState(false);
  const language = useAccountStore((state) => state.Language);
  const [, forceUpdate] = useReducer(function reloadFunc(x) {
    return x + 1;
  }, 0);
  const accountId = useAccountStore((state) => state._id);
  const accountTheme = useAccountStore((state) => state.AccountTheme);

  window.isRTL = language?.RTL;

  useEffect(function loadAccountData() {
    useAccountStore
      .getState()
      .load()
      .then(() => {
        useUserStore
          .getState()
          .setAccount(
            useAccountStore.getState()._id,
            useAccountStore.getState()?.AppConfig
          );
        (() => {
          const appata = JSON.parse(
            localStorage.getItem("Theme.App.Data") || "{}"
          );
          const theme = new Theme();
          theme.load(appata);
          const style = document.createElement("style");
          style.setAttribute("id", "apptheme");
          style.innerHTML = `.appTheme-app { ${theme.toCSS()} }`;
          document.head.append(style);
        })();

        return useUserStore.getState().load();
      })
      .then(() => {
        let currentUser = useUserStore.getState().currentUser;
        let accountDetails = currentUser?.AccountDetails;

        if (accountDetails) {
          getSandbox(accountDetails);
        }

        if (currentUser?.AppConfig) {
          let firebaseConfig = currentUser.AppConfig.Firebase;
          let pushNotificationConfig =
            currentUser.AppConfig.PushNotification.web;
          if (firebaseConfig) {
            rtdbClient.init(useAccountStore.getState()._id, firebaseConfig);
          }
          if (pushNotificationConfig) {
            pushNotificationClient.init(
              useAccountStore.getState()._id,
              pushNotificationConfig
            );
          }
        }
      })
      .catch((e) => {
        setFailStatus(true);
      });
    // .then(() => {
    //   if (ANON_URLS.includes(window.location.pathname)) {
    //     window.location.href = "/";
    //   } else {
    //     setReady(true);
    //   }
    // })
    // .catch((error) => {
    //   console.log("ERROR", error);
    //   if (!ANON_URLS.includes(window.location.pathname)) {
    //     // window.location.href = "/login";
    //   } else {
    //     setReady(true);
    //   }
    // });
  }, []);

  useEffect(
    function changeLanguage() {
      // if window.accountDetails.error_code is received on load, then we won't get lang info.
      // So here system will decide and load "en" messages.
      if (!language && !window.accountDetails?.error_code) {
        return;
      }
      let languageCode = language?.Name
        ? LANGUAGE_CODE[language.Name]
        : LANGUAGE_CODE.English;
      console.info("Trying to load language", language);
      // let baseUrl = useAccountStore.getState().AppConfig?.ResourcesPath; // to be rename
      // let buildNumber = getCookie("__TRANS_NUMBER");

      if (language?.RTL) {
        document.body.classList.add("rtl");
        document.querySelector("html").setAttribute("dir", "rtl");
        document.body.classList.add("ar");
      } else {
        document.body.classList.remove("rtl");
        document.querySelector("html").setAttribute("dir", "ltr");
        document.body.classList.remove("ar");
      }

      loadLanguage({
        locale: languageCode,
        kind: "web",
        onDone: () => {
          forceUpdate();
          setLanguageInitialized(true);
        },
        onError: () => {
          setLanguageInitialized(true);
        }
      });
    },
    [language]
  );

  useEffect(
    function applyingAccountTheme() {
      let theme = localStorage.getItem("Theme");
      onThemeChange(theme || ThemeList[1]._id);
      applyAccountTheme(
        accountTheme?.Colors?.[KEY_PRIMARY_500]?.Value,
        accountTheme?.ApplyBackground,
        accountTheme?.Font
      );
      accountTheme?.Font && loadFonts([accountTheme?.Font]);
    },
    [accountTheme]
  );

  useUnhandledRejectionListener();

  let childComponents = React.Children.toArray(children);
  let privateChildrens = childComponents.filter((comp) => {
    return comp.type === PrivateRoute;
  });
  let publicChildrens = childComponents.filter((comp) => {
    return comp.type === PublicRoute;
  });

  if (isFailed) {
    return (
      <React.Fragment>
        <I18nProvider>
          <BrowserRouter basename={baseName}>
            <Switch>
              <Route component={NotAvailable} />
            </Switch>
          </BrowserRouter>
        </I18nProvider>
      </React.Fragment>
    );
  }

  return accountId && isLanguageInitialized ? (
    <I18nProvider>
      <ConfigProvider direction={window.isRTL ? "rtl" : "ltr"}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={baseName}>
            <Switch>
              {LOGIN_ROUTES.map(({ path, component, hasAuth, ...etc }, i) => {
                let Component = component;
                return (
                  <LoginRoute key={path} path={path} {...etc}>
                    <Component />
                  </LoginRoute>
                );
              })}

              {PUBLIC_ROUTES.map(({ path, component, hasAuth, ...etc }, i) => {
                let Component = component;
                return (
                  <PublicRoute key={path} path={path} {...etc}>
                    <Component />
                  </PublicRoute>
                );
              })}

              {publicChildrens}
              <Route path="/">
                <AuthorizedComponents>{privateChildrens}</AuthorizedComponents>
              </Route>
            </Switch>
          </BrowserRouter>

          <ReactQueryDevTools />
        </QueryClientProvider>
      </ConfigProvider>
    </I18nProvider>
  ) : (
    ""
  );
}
